import PropTypes from "prop-types";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { navigate } from "gatsby";
import { useLocation } from "@gatsbyjs/reach-router";
import axios from "axios";
// eslint-disable-next-line import/no-unresolved
import { useIgCart } from "@intelligems/headless/gatsby";

import useDiscountsInfo from "@hooks/useDiscountsInfo";
import useGiftPackages from "@hooks/useGiftPackages";

import { GeoContext } from "@context/GeoProvider";
import StoreContext from "@context/store";
import { useTracking } from "@context/Tracking";
import { CART_UPDATE_EVENT_NAME } from "@context/blotoutShopWallet";

import DISCOUNT_TYPES from "@constants/DiscountTypes";
import { HandleType } from "@constants/HandleType";
import REDIRECT_TYPES from "@constants/RedirectType";
import GIFT_PACKAGES from "@constants/GiftPackages";

import { useALError } from "@helpers/ErrorBoundary/ALErrorBoundary";
import {
  isBrowser,
  getCookie,
  deleteCookie,
  getAllUrlParams,
  addURLParam,
  removeQueryParam,
  safeLocalStorage,
} from "@helpers/browser";
import { getShopifyObjectId, prepareAddToCart } from "@helpers/product";
import { getShopifyCartIdStorageKey, normalizeLines, isMTOOnlyCart } from "@helpers/cart";
import { LUXE_GIFT_LINE_ITEM_ATTRIBUTE_KEY } from "@helpers/luxe";
import { getMtoDeliveryWindow } from "@helpers/mto";

import {
  getEngravingText,
  getSelectedChar,
  getSelectedFont,
  INITIAL_NECKLACE_FONT,
} from "@components/ProductPage/mto/mtoEngraving";

export const defaultCartObject = {
  lines: [],
  discountCodes: [],
  isDefaultObject: true,
  note: "",
};
const defaultCartContext = {
  cart: defaultCartObject,
  isLoadingCart: false,
};

const EXCLUDE_ITEMS_TAGS = [
  "GIVES BACK",
  "Type:Accessories",
  "bundle",
  "mystery",
  "giftcard",
  "subscription",
  "Type:Earring Charms",
];

// Customer facing keys of the made-to-order engraving, shown in the cart and on the order
const ENGRAVING_ATTRIBUTE_KEY = "Engraving";
const BACK_ENGRAVING_ATTRIBUTE_KEY = "Back Engraving";
const FONT_ATTRIBUTE_KEY = "Font";

export const CartContext = createContext(defaultCartContext);

export function buildAttributes(lines = []) {
  return lines.map((line) => {
    const { attributesProperties } = line;
    if (attributesProperties) {
      // Pre-order
      if (attributesProperties.isPreOrder) {
        line.attributes.push(
          { key: "_pre_order", value: "true" },
          {
            key: "_ships_by_pre_order",
            value: attributesProperties.preOrderShipDate,
          }
        );
      }
      // Made To Order
      if (attributesProperties.mtoTimeline) {
        const { from, to } = getMtoDeliveryWindow(attributesProperties.mtoTimeline) || {};
        line.attributes.push(
          { key: "_mto", value: "true" },
          {
            key: "Made to Order",
            value: `This item is custom-made and will arrive from ${from} - ${to}`,
          },
          { key: "_mto_timeline", value: attributesProperties.mtoTimeline }
        );
      }

      // The customizer the product is built through (single_engraving, initial_necklace, ...), so
      // the customization attributes below can be read for what they describe
      if (attributesProperties.mtoType) {
        line.attributes.push({ key: "_mto_type", value: attributesProperties.mtoType });
      }

      // Made To Order custom settings (stones, engraving, ...)
      if (attributesProperties.mtoSettings) {
        let hasCustomSetting = false;

        // Products engraving character by character (initial necklace, single engraving, ...) hold
        // one `single_char` setting per slot, and what they engrave is those characters read in
        // order rather than the free text of the font setting
        const charsText = Object.values(attributesProperties.mtoSettings)
          .filter((s) => s?.type === "single_char")
          .map((s) => getSelectedChar(s))
          .join("");

        Object.entries(attributesProperties.mtoSettings).forEach(([settingId, s]) => {
          const variant = s?.variants?.find((v) => v.isSelected);
          if (variant) {
            hasCustomSetting = true;
            line.attributes.push(
              { key: s.name, value: variant.name },
              { key: `_mto_variant_id_${settingId}`, value: variant.id },
              { key: `_mto_variant_name_${settingId}`, value: variant.name }
            );
          }

          // Free text engraving: the setting carries no variant, the text lives on the fonts and
          // the font the customer picked is what the workshop engraves it with. `back_engraving`
          // is the same thing engraved on the back, and is priced through a "Yes"/"No" variant
          if (s?.type === "single_char") {
            const char = getSelectedChar(s);
            if (char) {
              hasCustomSetting = true;
              // The position of each character matters to the workshop, so every slot is kept
              // apart on its own attribute on top of the whole text
              line.attributes.push({ key: `_mto_char_${settingId}`, value: char });
            }
          }

          if (s?.type === "font_engraving" || s?.type === "back_engraving") {
            // The characters picked one by one take precedence over the free text, the same way
            // the preview renders them (`back_engraving` is always free text)
            const engravingText =
              (s.type === "font_engraving" ? charsText : "") || getEngravingText(s);
            const font = getSelectedFont(s);
            if (engravingText) {
              hasCustomSetting = true;
              line.attributes.push(
                {
                  key:
                    s.type === "back_engraving"
                      ? BACK_ENGRAVING_ATTRIBUTE_KEY
                      : ENGRAVING_ATTRIBUTE_KEY,
                  value: engravingText,
                },
                { key: `_mto_engraving_${settingId}`, value: engravingText }
              );
              // Only settings offering a real choice surface the font to the customer, and only
              // once something is engraved with it
              if (font && (s.fonts?.length ?? 0) > 1) {
                line.attributes.push({ key: s.title || FONT_ATTRIBUTE_KEY, value: font.name });
              }
            }

            // The workshop needs the font the customer picked, so it is kept on the hidden
            // attributes whether or not the engraving is filled in. Nothing is engraved on the
            // back without a text though, so `back_engraving` only carries its font along with it
            if (font && (s.type === "font_engraving" || engravingText)) {
              line.attributes.push(
                { key: `_mto_font_family_${settingId}`, value: font.font_family },
                { key: `_mto_font_name_${settingId}`, value: font.name }
              );
            }
          }
        });

        // A product engraving characters without offering a font still has to carry what is
        // engraved on the line, with the font its preview is drawn with
        if (
          charsText &&
          !Object.values(attributesProperties.mtoSettings).some((s) => s?.type === "font_engraving")
        ) {
          line.attributes.push(
            { key: ENGRAVING_ATTRIBUTE_KEY, value: charsText },
            { key: "_mto_engraving", value: charsText },
            { key: "_mto_font_family", value: INITIAL_NECKLACE_FONT.font_family },
            { key: "_mto_font_name", value: INITIAL_NECKLACE_FONT.name }
          );
        }

        // to easily identify MTO with custom settings in cart
        if (hasCustomSetting) {
          line.attributes.push({ key: "_mto_custom", value: "true" });
        }
      }
      // Prices info
      if (
        attributesProperties.isUS &&
        attributesProperties.prices &&
        "finalPrice" in attributesProperties.prices &&
        "fullPrice" in attributesProperties.prices
      ) {
        line.attributes.push(
          { key: "_customer_currency", value: attributesProperties?.currencyCode || "" },
          {
            key: "_customer_discount_value",
            value:
              (
                parseFloat(attributesProperties.prices?.fullPrice) -
                parseFloat(attributesProperties.prices?.finalPrice)
              ).toString() || "",
          }
        );
      }

      // Final Sale tag
      if (attributesProperties.isFinalSale) {
        line.attributes.push({ key: "_final_sale", value: "true" });
      }

      // IG Prices
      if (attributesProperties.igPrices?.isIgPrice && attributesProperties.prices?.finalPrice) {
        line.attributes.push({
          key: "_igp",
          value: (Number(attributesProperties.prices.finalPrice) * 100).toFixed(0), // to string integer (no decimal)
        });
      }

      // Impact Radius
      const impactClickId = getCookie("_irclickid");
      if (impactClickId) {
        line.attributes.push({ key: "_irclickid", value: impactClickId });
      }
      // Bundle Name
      if (attributesProperties.bundleName) {
        line.attributes.push({ key: "_bundle_name", value: attributesProperties.bundleName });
      }

      // BYOB
      if (attributesProperties.isByob) {
        line.attributes.push({ key: "_isBYOB", value: "true" });
        if (attributesProperties.collectionHandle) {
          line.attributes.push({
            key: "_collectionHandle",
            value: attributesProperties.collectionHandle,
          });
        }
      }
    }
    return line;
  });
}

function setCartId(cartLocalStorageKey, cartId, sendReport = () => {}) {
  if (isBrowser) {
    safeLocalStorage.setItem(cartLocalStorageKey, cartId);

    // Track cart event - help CRM to retrieve cart
    if (window.exponea) {
      window.exponea.track("track_cart", {
        cartId: encodeURIComponent(cartId),
      });

      window.exponea.identify(
        {
          cart_id: encodeURIComponent(cartId),
        },
        {
          cart_id: encodeURIComponent(cartId),
        }
      );
    }

    // Shopify cookie used for checkout.analuisa.com/checkout redirection
    // https://www.shopify.com/legal/cookies#merchant-storefronts
    const expiration = new Date();
    expiration.setDate(expiration.getDate() + 14); // Shopify seems to set their cookie to expire in 2 weeks
    document.cookie = `cart=${cartId.replace(
      "gid://shopify/Cart/",
      ""
    )};expires=${expiration.toISOString()};path=/;domain=analuisa.com`;

    try {
      // /!\ Do not remove, this is used for avoid crashes on old versions of the cart
      document.cookie.split("; ").forEach((cookie) => {
        const [key] = cookie.split("=");
        if (key.startsWith("gid://shopify/Cart")) {
          deleteCookie(key);
        }
      });
    } catch (error) {
      sendReport(error, { name: "CartProvider", priority: "P1" });
    }
  }
}

function prepareCartForAddToCartExponea(cart, productsToAdd = [], componentType = "") {
  const productIds = [];
  const productList = [];
  const imageList = [];
  const skuIds = [];
  const skuList = [];
  const variantIds = [];
  const variantList = [];
  const { lines } = cart;
  let totalPrice = 0;
  let totalPriceLocalCurrency = 0;
  let totalQuantity = 0;

  if (lines && lines.length > 0) {
    lines.forEach((p) => {
      let isMto = false;
      if (p?.attributes) {
        isMto = !!p.attributes.find((atr) => atr.key === "_mto" && atr.value === "true");
      }
      if (isMto) {
        const selectedVariantIdsStr = p?.attributes
          .filter((a) => a.key.includes("_mto_variant_id_"))
          .map((a) => a.value)
          .join(",");

        imageList.push(
          `https://www.analuisa.com/.netlify/functions/custom-mto-svg/${p.variant.product.handle}?selectedVariantIds=${selectedVariantIdsStr}`
        );
      } else {
        imageList.push(p?.variant?.image?.url);
      }

      const productId = getShopifyObjectId(p.variant.product.id);
      const variantId = getShopifyObjectId(p.variant.id);
      const qty = p.quantity;
      totalQuantity += qty;
      productList.push({
        product_id: productId,
        quantity: qty,
      });
      skuList.push({
        quantity: qty,
        sku: p.variant.sku,
      });
      variantList.push({
        quantity: qty,
        variant_id: variantId,
      });

      totalPrice += parseFloat(p.variant.price.amount);
      totalPriceLocalCurrency += parseFloat(p.variant.price.amount);

      skuIds.push(p.variant.sku);
      productIds.push(productId);
      variantIds.push(variantId);
    });
  }

  // productIds: [6832288727239, 6804546126023, 4684363235402]
  // productList: [{"product_id":"6832288727239","quantity":1}, {"product_id":"6804546126023","quantity":2}, {"product_id":"4684363235402","quantity":1}]
  // skuIds: [C-191315, 310001-151-20-1, 100020-710-01-2]
  // skuList: [{"quantity":1,"sku":"C-191315"}, {"quantity":1,"sku":"310001-151-20-1"}, {"quantity":2,"sku":"100020-710-01-2"}]
  // totalPrice: 303
  // totalPriceLocalCurrency: 303
  // totalQuantity: 4
  // variantIds: [40357760401607, 40256421200071, 32337754619978]
  // variantList: [{"quantity":1,"variant_id":"40357760401607"}, {"quantity":2,"variant_id":"40256421200071"}, {"quantity":1,"variant_id":"32337754619978"}]
  const linesStr = cart?.lines
    ?.filter((line) => line.variant.sku !== "L-0001") // exclude Luxe
    ?.map((line) => {
      const id = line?.variant?.id.replace("gid://shopify/ProductVariant/", "");
      return `${id};${line?.quantity}`;
    })
    .join(",");
  const addedProducts = lines?.filter((line) =>
    productsToAdd?.some((p) => p?.variant?.product?.handle === line?.variant?.product?.handle)
  );
  // productsToAdd contains all the products in a bundle
  const bundleName =
    productsToAdd[0].attributes.find(({ key }) => key === "_bundle_name")?.value || null;
  return {
    product_ids: productIds,
    product_id: getShopifyObjectId(
      addedProducts?.find((line) => line?.variant?.product?.handle !== "al-luxe")?.variant?.product
        ?.id || ""
    ), // arbitrary take the first non-al-luxe product
    product_list: productList,
    sku_ids: skuIds,
    sku_list: skuList,
    variant_ids: variantIds,
    variant_list: variantList,
    image_list: imageList,
    total_price: totalPrice,
    total_price_local_currency: totalPriceLocalCurrency,
    total_quantity: totalQuantity,
    linesStr,
    component_name: componentType,
    category:
      addedProducts?.find((line) => line?.variant?.product?.handle !== "al-luxe")?.variant?.product
        ?.productType || "", // arbitrary take the first non-al-luxe product
    bundle: bundleName
      ? [{ bundleName, products: productsToAdd.map((p) => p?.variant?.product?.title) }]
      : null,
  };
}

function CartProvider({ children }) {
  const location = useLocation();
  const { sendReport } = useALError();
  const { trackAddToCart, trackRemoveFromCart, trackAddToCartExponea, trackAddFreeGift } =
    useTracking();
  const { gePriceDetails, clientIp } = useContext(GeoContext);
  const discountsInfo = useDiscountsInfo();
  const { searchIndex } = useContext(StoreContext);

  const [cart, setCart] = useState(defaultCartObject);
  const [isLoadingCart, setIsLoadingCart] = useState(false);
  const [isSideCartOpen, setIsSideCartOpen] = useState(false);

  const countryCode = gePriceDetails?.CountryCode;
  const currencyCode = gePriceDetails?.CurrencyCode;

  const { wrapCustomAttributes } = useIgCart(cart?.cartId);
  const { products: giftPackagesProducts } = useGiftPackages();

  const params = useMemo(() => getAllUrlParams(location.href), [location.href]);

  useEffect(() => {
    // Open cart from cart params
    if (isBrowser && params?.cart) {
      setIsSideCartOpen(true);
    }
    // Replace the URL with the checkout URL
    if (params?.redirect === REDIRECT_TYPES.GO_TO_CHECKOUT && cart.checkoutUrl) {
      removeQueryParam("redirect");
      addURLParam("redirect", cart.checkoutUrl);
      navigate(window.location.href.split(window.location.origin)[1]);
    }
  }, [cart.checkoutUrl, params]);

  useEffect(() => {
    // when the sidecart is open, add a "cart=true" query param to the URL
    if (isBrowser && isSideCartOpen && !params?.cart) {
      addURLParam("cart", "true");
    } else {
      removeQueryParam("cart");
    }
  }, [isSideCartOpen, params?.cart]);

  const toggleCart = useCallback(() => {
    setIsSideCartOpen((prev) => !prev);
  }, []);

  const quantityPercentDiscount = discountsInfo.find(
    (d) => d.type === DISCOUNT_TYPES.QUANTITY_PERCENT
  );

  const cartLocalStorageKey = useMemo(() => getShopifyCartIdStorageKey(countryCode), [countryCode]);

  const retrieveCart = useCallback(async () => {
    const existingCartId = isBrowser ? safeLocalStorage.getItem(cartLocalStorageKey) : null;
    try {
      setIsLoadingCart(true);
      const res = await axios.get("/.netlify/functions/get-cart", {
        params: {
          cartId: existingCartId,
          countryCode,
        },
        headers: { ip_address: clientIp }, // allow clientIp to not be filled for perf purposes
      });
      if (res.status !== 200) {
        throw new Error(`Received invalid response status fro get-cart (status: ${res.status})`);
      }
      setCart(res.data);
      setCartId(cartLocalStorageKey, res.data.cartId, sendReport);
    } catch (error) {
      console.error("Network HTTP status (only if network error): ", error?.response?.status);
      console.error("Network HTTP data (only if network error): ", error?.response?.data);
      console.error(error);
      sendReport(error, { name: "CartProvider", priority: "P1" });
    } finally {
      setIsLoadingCart(false);
    }
  }, [cartLocalStorageKey, countryCode, sendReport]); // Do not track change of clientIp

  // Whenever retrieveCart changes (its dependencies change),
  // check if there's a cartId in url params and use that, overriding the existing one.
  // If there isn't one, recall retrieveCart anyway
  useEffect(() => {
    // Check if there's a cartId in url params
    if (isBrowser && params?.cartId) {
      // Set it to local storage
      safeLocalStorage.setItem(
        cartLocalStorageKey,
        params.cartId.includes("gid:")
          ? decodeURIComponent(params.cartId)
          : `gid://shopify/Cart/${params.cartId}`
      );

      // Delete the cartId from url params
      removeQueryParam("cartId");
    }

    retrieveCart();
  }, [cartLocalStorageKey, params?.cartId, retrieveCart]);

  /**
   * Handles the browser's pageshow event to refresh the cart when the page is restored from the bfcache.
   * This ensures the cart stays up-to-date even when navigating back from checkout.
   */
  useEffect(() => {
    if (!isBrowser) {
      return undefined;
    }
    const handlePageShow = (event) => {
      if (event.persisted) {
        retrieveCart();
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, [retrieveCart]);

  const removeManyProductsFromCart = useCallback(
    async ({
      linesToRemove,
      customProperties = {},
      cartId = null,
      shouldOpenSideCart = true,
      discountCode: overridingDiscountCode = undefined,
    }) => {
      if (isLoadingCart) return;
      try {
        setIsLoadingCart(true);
        if (shouldOpenSideCart) {
          setIsSideCartOpen(true);
        }

        const data = {
          ...customProperties,
          cartLinesId: linesToRemove.map((line) => line.id),
          cartId: cartId || cart.cartId,
          countryCode,
          discountCode: overridingDiscountCode,
        };
        const res = await axios.delete("/.netlify/functions/remove-product-from-cart", {
          data,
          headers: { ip_address: clientIp },
        });
        if (res.status !== 200) {
          throw new Error(
            `removeManyProductsFromCart(): Invalid response status from functions/remove-product-from-cart: ${res.status}`
          );
        }
        setCart(res.data);
        setCartId(cartLocalStorageKey, res.data.cartId, sendReport);

        // Collect all productIds currently in the cart after removal
        const productIds = Array.isArray(res?.data?.lines)
          ? res.data.lines.map((line) => getShopifyObjectId(line.variant.product.id))
          : [];

        linesToRemove.forEach((lineRemoved) => {
          const { price, product } = lineRemoved.variant;
          trackRemoveFromCart({
            price,
            title: product.title,
            category: product.productType,
            quantity: lineRemoved.quantity,
            lineItem: lineRemoved,
            productId: getShopifyObjectId(lineRemoved.variant.product.id),
            variantId: getShopifyObjectId(lineRemoved.variant.id),
            checkoutUrl: res?.data?.checkoutUrl || "",
            productHandle: product.handle,
            product_ids: productIds, // Bloomreach needs product_ids for remove
          });
        });
        return res.data;
      } catch (error) {
        console.error(error);
        sendReport(error, { name: "Provider", priority: "P1" });
      } finally {
        setIsLoadingCart(false);
      }
    },
    [
      isLoadingCart,
      cart.cartId,
      countryCode,
      clientIp,
      cartLocalStorageKey,
      trackRemoveFromCart,
      sendReport,
    ]
  );

  const removeSingleProductsFromCart = useCallback(
    async ({
      lineToRemove,
      customProperties = {},
      cartId = null,
      shouldOpenSideCart = true,
      discountCode: overridingDiscountCode = undefined,
    }) =>
      removeManyProductsFromCart({
        linesToRemove: [lineToRemove],
        customProperties,
        cartId,
        shouldOpenSideCart,
        discountCode: overridingDiscountCode,
      }),
    [removeManyProductsFromCart]
  );
  /**
   * Add a list of products to the cart
   * @param {Object[]} dataProductsToAdd - Array using the same format as cart line items
   * @param {Object=} customProperties - Optional additional data sent to the lambda
   */
  const addManyProductsToCart = useCallback(
    async ({
      dataProductsToAdd,
      customProperties = {},
      cartId = null,
      shouldOpenSideCart = true,
      discountCode: overridingDiscountCode = undefined,
      componentType = "", // for tracking purposes
    }) => {
      if (isLoadingCart) return null;
      const freeGiftInCurrentCart = cart.lines.find((line) =>
        line.attributes.some(({ key, value }) => key === "_free_gift" && value === "true")
      );
      try {
        setIsLoadingCart(true);
        if (shouldOpenSideCart) {
          setIsSideCartOpen(true);
        }

        let productsToAdd = buildAttributes(dataProductsToAdd);
        productsToAdd = normalizeLines(dataProductsToAdd);
        productsToAdd = productsToAdd.map(({ trackingProperties, ...line }) => {
          try {
            const {
              variant: { id: variantId, product },
              attributes,
            } = line;

            wrapCustomAttributes({
              productId: product.id,
              variantId,
              customAttributes: attributes,
            });
          } catch (error) {
            console.error("failed to track Add to Cart for:", line);
            sendReport(error, { name: "Provider", priority: "P1" });
          }
          return { ...line, ...trackingProperties };
        });
        const body = {
          ...customProperties,
          countryCode,
          cartId: cartId || cart.cartId,
          lines: productsToAdd,
          discountCode: overridingDiscountCode,
        };
        const res = await axios.post("/.netlify/functions/add-to-cart", body, {
          headers: { ip_address: clientIp },
        });

        productsToAdd.forEach((line) => {
          const {
            variant: { price, id: variantId, product, sku },
            attributes,
            quantity,
            shouldTrackAlgolia,
            source,
          } = line;

          const isFreeGift = attributes.some(
            ({ key, value }) => key === "_free_gift" && value === "true"
          );
          if (product.handle !== "al-luxe" && !isFreeGift && price && product.title) {
            trackAddToCart({
              price,
              title: product.title,
              variantId: getShopifyObjectId(variantId),
              productId: getShopifyObjectId(product.id),
              sku,
              category: product.productType,
              quantity,
              currency: currencyCode,
              productHandle: product.handle,
              shouldTrackAlgolia,
              index: searchIndex,
              isPreOrder: attributes.some(({ key, value }) => key === "pre_order" && value),
              preOrderShipDate: attributes.find(({ key }) => key === "_ships_by_pre_order")?.value,
              source,
              images: product.images,
              checkoutUrl: res?.data?.checkoutUrl || "",
            });
          }
        });

        if (res.status !== 200) {
          console.error("body sent:", body);
          throw new Error(
            `addManyProductsToCart(): Invalid response status from functions/add-to-cart: ${res.status}`
          );
        }
        setCart(res.data);
        setCartId(cartLocalStorageKey, res.data.cartId, sendReport);

        const freeGiftInUpdatedCart = res.data.lines.find((line) =>
          line.attributes.some(({ key, value }) => key === "_free_gift" && value === "true")
        );

        if (!freeGiftInCurrentCart && freeGiftInUpdatedCart) {
          const freeGiftProduct = res.data.lines.find((line) =>
            line.attributes.some(({ key, value }) => key === "_free_gift" && value === "true")
          );
          trackAddFreeGift({
            productId: freeGiftProduct.variant.product.id,
            productTitle: freeGiftProduct.variant.product.title,
            productHandle: freeGiftProduct.variant.product.handle,
            type: "auto_add",
          });
        }

        // Trigger ATC events exponea
        try {
          trackAddToCartExponea(
            prepareCartForAddToCartExponea(res.data, productsToAdd, componentType)
          );
        } catch (error) {
          console.log(error);
          sendReport(error, { name: "CartProvider", priority: "P1" });
        }
        return res.data;
      } catch (error) {
        console.error(error);
        sendReport(error, { name: "CartProvider", priority: "P1" });
        return null;
      } finally {
        setIsLoadingCart(false);
      }
    },
    [
      isLoadingCart,
      countryCode,
      cart,
      clientIp,
      cartLocalStorageKey,
      trackAddToCart,
      currencyCode,
      searchIndex,
      wrapCustomAttributes,
      sendReport,
      trackAddToCartExponea,
      trackAddFreeGift,
    ]
  );

  const addSingleProductToCart = useCallback(
    async ({
      dataProductToAdd,
      customProperties = {},
      cartId = null,
      shouldOpenSideCart = true,
      componentType = "",
    }) =>
      addManyProductsToCart({
        dataProductsToAdd: [dataProductToAdd],
        customProperties,
        cartId,
        shouldOpenSideCart,
        componentType,
      }),
    [addManyProductsToCart]
  );

  const resetCart = useCallback(() => {
    const shopifyCheckoutIdStorageKey = getShopifyCartIdStorageKey(countryCode);
    safeLocalStorage.removeItem(shopifyCheckoutIdStorageKey);
    setCart(defaultCartObject);
  }, [countryCode]);

  const itemsInCart = useMemo(
    () =>
      cart.lines.reduce((prev, p) => {
        if (
          p?.variant?.product?.handle !== HandleType.ORDER_PROTECTION &&
          p?.variant?.product?.handle !== HandleType.SUBSCRIPTION && // TO update on pre-prod with proper product handle
          !p?.attributes?.some(
            ({ key, value }) => key === LUXE_GIFT_LINE_ITEM_ATTRIBUTE_KEY && value === "true"
          )
        ) {
          return prev + p.quantity;
        }
        return prev;
      }, 0),
    [cart]
  );

  const updateCartAttributes = useCallback(
    async ({ customAttributes, customCartId = null }) => {
      if (isLoadingCart || !(cart?.cartId || customCartId)) return;
      try {
        setIsLoadingCart(true);
        const res = await axios.post(
          "/.netlify/functions/update-cart-attributes",
          {
            cartId: customCartId || cart.cartId,
            countryCode,
            attributes: customAttributes,
          },
          {
            headers: { ip_address: clientIp },
          }
        );
        if (res.status !== 200) {
          throw new Error(
            `updateCartAttributes(): Invalid response status from functions/update-cart-attributes: ${res.status}`
          );
        }
        setCart(res.data);
        setCartId(cartLocalStorageKey, res.data.cartId, sendReport);
      } catch (error) {
        console.error(error);
        sendReport(error, { name: "Provider", priority: "P1" });
      } finally {
        setIsLoadingCart(false);
      }
    },
    [isLoadingCart, cart, countryCode, cartLocalStorageKey, sendReport]
  );

  const updateCartNote = useCallback(
    async (note, customCartId = null) => {
      if (note == null || typeof note === "undefined") {
        return null;
      }
      try {
        const res = await axios.post(
          "/.netlify/functions/update-cart-note",
          {
            cartId: customCartId || cart.cartId,
            countryCode,
            note,
          },
          {
            headers: { ip_address: clientIp },
          }
        );
        if (res.status !== 200) {
          throw new Error(
            `updateCartNote(): Invalid response status from functions/update-cart-note: ${res.status}`
          );
        }
        setCart(res.data);
        return res.data;
      } catch (error) {
        console.error(error);
        sendReport(error, { name: "Provider", priority: "P1" });
        throw error;
      }
    },
    [countryCode, clientIp, sendReport, cart.cartId]
  );

  // Sync Gorgias chat attributes onto the cart once it's loaded.
  // The Gorgias bridge script may load after the cart, so we retry briefly until it's ready.
  useEffect(() => {
    if (!isBrowser || !cart?.cartId) return undefined;

    let timeoutId;
    let attempts = 0;
    const trySync = () => {
      let gorgiasAttributes;
      try {
        gorgiasAttributes = window.GorgiasBridge?.createCartAttributes?.();
      } catch (e) {
        // GorgiasBridge exists but isn't initialized yet — it throws on property access.
        gorgiasAttributes = undefined;
      }
      if (!gorgiasAttributes?.length) {
        if (attempts < 10) {
          attempts += 1;
          timeoutId = setTimeout(trySync, 500);
        }
        return;
      }
      const hasAllGorgiasAttributes = gorgiasAttributes.every((ga) =>
        (cart.attributes || []).some((a) => a.key === ga.key && a.value === ga.value)
      );
      if (!hasAllGorgiasAttributes) {
        updateCartAttributes({
          customAttributes: [...(cart.attributes || []), ...gorgiasAttributes],
        });
      }
    };
    trySync();
    return () => clearTimeout(timeoutId);
  }, [cart?.cartId, cart?.attributes, updateCartAttributes]);

  const addGiftPackageToCart = useCallback(
    (handle) => {
      const giftPackageData = giftPackagesProducts.find((product) => product.handle === handle);
      if (!giftPackageData) return;
      const { dataProductsToAdd } = prepareAddToCart({
        product: giftPackageData.shopify,
        selectedVariant: giftPackageData.shopify.variants[0],
        productPrices: giftPackageData,
        countryCode,
        currencyCode,
        discountsInfo,
      });
      addManyProductsToCart({ dataProductsToAdd, shouldOpenSideCart: false });
    },
    [giftPackagesProducts, countryCode, currencyCode, discountsInfo, addManyProductsToCart]
  );

  useEffect(() => {
    if (isLoadingCart) {
      return;
    }

    const isMTOCart = isMTOOnlyCart(cart);
    const mtoGiftPackagesInCart = cart.lines.filter((line) =>
      GIFT_PACKAGES.MTO.includes(line.variant.product.handle)
    );
    const regularGiftPackagesInCart = cart.lines.filter((line) =>
      GIFT_PACKAGES.CLASSIC.includes(line.variant.product.handle)
    );

    const swapGiftPackage = async (linesToRemove, handleToAdd) => {
      const updatedCart = await removeManyProductsFromCart({
        linesToRemove,
        shouldOpenSideCart: false,
      });
      if (!updatedCart) {
        return;
      }
      addGiftPackageToCart(handleToAdd);
    };

    if (!isMTOCart && mtoGiftPackagesInCart.length) {
      swapGiftPackage(mtoGiftPackagesInCart, GIFT_PACKAGES.CLASSIC[1]);
    } else if (isMTOCart && regularGiftPackagesInCart.length) {
      swapGiftPackage(regularGiftPackagesInCart, GIFT_PACKAGES.MTO[0]);
    }
  }, [cart, isLoadingCart, removeManyProductsFromCart, addGiftPackageToCart]);

  const discountStatus = useMemo(() => {
    if (!quantityPercentDiscount) {
      return null;
    }

    const totalEligibleItems = cart?.lines.reduce((total, line) => {
      const hasExcludedTag = line.variant.product.tags.some((tag) =>
        EXCLUDE_ITEMS_TAGS.includes(tag)
      );
      return hasExcludedTag ? total : total + line.quantity;
    }, 0);

    const getValuesDiscount = (d, nbItems) => {
      // If nbItems is 0 and the first threshold in d is greater than 1, return 0
      if (nbItems + 1 < d[0][0]) {
        return {
          current: null,
          next: null,
        };
      }

      // Find next discount to be applied (outside Luxe Extra discount)
      const next = d.find(([threshold]) => nbItems + 1 <= threshold) || d[d.length - 1];

      // Find current applied discount
      let current = null;
      d.forEach((discount, i) => {
        if (i + 1 === d.length && nbItems >= discount[0]) {
          // last element
          current = discount;
        } else if (nbItems === discount[0]) {
          current = discount;
        }
      });

      return {
        next: next === current ? null : next,
        current,
      };
    };

    return getValuesDiscount(quantityPercentDiscount.discounts, totalEligibleItems);
  }, [cart?.lines, quantityPercentDiscount]);

  const cartData = useMemo(
    () => ({
      cart,
      isLoadingCart,
      addManyProductsToCart,
      addSingleProductToCart,
      removeManyProductsFromCart,
      removeSingleProductsFromCart,
      getShopifyCartIdStorageKey,
      resetCart,
      itemsInCart,
      isSideCartOpen,
      setIsSideCartOpen,
      toggleCart,
      discountStatus,
      updateCartAttributes,
      updateCartNote,
    }),
    [
      addManyProductsToCart,
      addSingleProductToCart,
      cart,
      isLoadingCart,
      removeManyProductsFromCart,
      removeSingleProductsFromCart,
      resetCart,
      itemsInCart,
      isSideCartOpen,
      setIsSideCartOpen,
      toggleCart,
      discountStatus,
      updateCartAttributes,
      updateCartNote,
    ]
  );

  useEffect(() => {
    if (isBrowser) {
      // Blotout cart recovery sync
      const handleCartUpdate = (event) => {
        const newCart = event.detail;
        setCart(event.detail);
        newCart.lines.forEach((line) => {
          const {
            variant: { price, id: variantId, product, sku, image },
            attributes,
            quantity,
          } = line;

          if (product.handle !== "al-luxe") {
            trackAddToCart({
              price: Number(price.amount),
              title: product.title,
              variantId: getShopifyObjectId(variantId),
              productId: getShopifyObjectId(product.id),
              sku,
              category: product.productType,
              quantity,
              currency: price.currencyCode,
              productHandle: product.handle,
              shouldTrackAlgolia: false,
              index: null,
              isPreOrder: attributes.some(({ key, value }) => key === "pre_order" && value),
              preOrderShipDate: attributes.find(({ key }) => key === "_ships_by_pre_order")?.value,
              source: null,
              images: [image],
              checkoutUrl: newCart.checkoutUrl || "",
            });
          }
        });
        trackAddToCartExponea(prepareCartForAddToCartExponea(newCart));
      };
      window.addEventListener(CART_UPDATE_EVENT_NAME, handleCartUpdate);
      return () => {
        window.removeEventListener(CART_UPDATE_EVENT_NAME, handleCartUpdate);
      };
    }
  }, [trackAddToCart, trackAddToCartExponea]);

  return <CartContext.Provider value={cartData}>{children}</CartContext.Provider>;
}

CartProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default CartProvider;
